import React, { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { getThemeColors } from '../../utils/themeStyles';
import { COUNTRIES } from '../../config/countries';

// Connexion / inscription Entra External ID — OTP email saisi DANS l'app.
// 3 étapes : email -> (si nouveau) formulaire attributs obligatoires -> OTP.
export default function LoginScreen() {
  const { startEmailOtp, startSignup, submitOtp, loginWithGoogle } = useAuth();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);

  const [step, setStep] = useState<'email' | 'signup' | 'otp'>('email');
  const [fromSignup, setFromSignup] = useState(false); // OTP issu d'une inscription ?
  const [email, setEmail] = useState('');
  const [otpTarget, setOtpTarget] = useState(''); // cible masquée renvoyée par Entra (challenge_target_label)
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Attributs d'inscription (obligatoires)
  const [givenName, setGivenName] = useState('');
  const [surname, setSurname] = useState('');
  const [age, setAge] = useState('');
  const [country, setCountry] = useState(''); // code ISO
  const [countryQuery, setCountryQuery] = useState('');
  const [countryOpen, setCountryOpen] = useState(false);

  const countryName = useMemo(
    () => COUNTRIES.find(c => c.code === country)?.name ?? '',
    [country],
  );
  const filteredCountries = useMemo(() => {
    const q = countryQuery.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(c => c.name.toLowerCase().includes(q) || c.code.toLowerCase() === q);
  }, [countryQuery]);

  const sendCode = async () => {
    const e = email.trim();
    if (!e || !e.includes('@')) {
      setError('Veuillez saisir un email valide.');
      return;
    }
    setError('');
    setBusy(true);
    setFromSignup(false);
    try {
      const r = await startEmailOtp(e);
      setOtpTarget(r.target ?? '');
      setStep(r.mode === 'signup' ? 'signup' : 'otp');
    } catch (err: any) {
      setError(err?.message ? String(err.message) : 'Opération impossible.');
    } finally {
      setBusy(false);
    }
  };

  const submitSignup = async () => {
    if (!givenName.trim() || !surname.trim()) {
      setError('Prénom et nom sont obligatoires.');
      return;
    }
    const ageNum = parseInt(age, 10);
    if (!age.trim() || Number.isNaN(ageNum) || ageNum < 1 || ageNum > 120) {
      setError('Veuillez saisir un âge valide.');
      return;
    }
    if (!country) {
      setError('Veuillez sélectionner un pays.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      const res = await startSignup(email.trim(), {
        givenName: givenName.trim(),
        surname: surname.trim(),
        age: String(ageNum),
        country,
      });
      setOtpTarget(res?.target ?? '');
      setFromSignup(true);
      setStep('otp');
    } catch (err: any) {
      setError(err?.message ? String(err.message) : 'Inscription impossible.');
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    const c = code.trim();
    if (!c) {
      setError('Veuillez saisir le code reçu.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      // À l'inscription, on transmet le profil -> créé/MAJ en base (age inclus).
      const profile = fromSignup
        ? { givenName: givenName.trim(), surname: surname.trim(), age: age.trim(), country }
        : undefined;
      await submitOtp(c, profile); // succès -> AuthContext définit user -> bascule vers l'app
    } catch (err: any) {
      setError(err?.message ? String(err.message) : 'Code invalide.');
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setBusy(true);
    try {
      // Web : redirige vers Google (ne résout pas). Natif : Custom Tab puis session.
      await loginWithGoogle();
      // Succès natif -> AuthContext définit user -> ce composant est démonté.
    } catch (err: any) {
      setError(err?.message ? String(err.message) : 'Connexion Google impossible.');
      setBusy(false);
    }
  };

  const field = (
    placeholder: string,
    value: string,
    onChange: (t: string) => void,
    extra?: object,
  ) => (
    <View style={[styles.inputWrapper, { borderBottomColor: colors.inputBorder }]}>
      <TextInput
        style={[styles.input, { color: colors.inputText }]}
        placeholder={placeholder}
        placeholderTextColor={colors.inputPlaceholder}
        value={value}
        onChangeText={t => {
          onChange(t);
          setError('');
        }}
        editable={!busy}
        {...extra}
      />
    </View>
  );

  return (
    <KeyboardAvoidingView
      style={[styles.root, { backgroundColor: colors.background }]}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
        <View style={styles.card}>
          {step === 'email' && (
            <>
              <Text style={[styles.title, { color: colors.textPrimary }]}>Se connecter</Text>
              <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
                Saisissez votre email : un code de vérification vous sera envoyé. Nouveau ? Votre
                compte sera créé.
              </Text>
              {field('Email', email, setEmail, {
                autoCapitalize: 'none',
                keyboardType: 'email-address',
                returnKeyType: 'send',
                onSubmitEditing: sendCode,
              })}
              {error ? <Text style={[styles.errorText, { color: colors.error }]}>{error}</Text> : null}
              <TouchableOpacity
                style={[styles.btn, { backgroundColor: colors.primary }, busy && styles.btnDisabled]}
                onPress={sendCode}
                disabled={busy}
                activeOpacity={0.8}
              >
                {busy ? <ActivityIndicator color="#fff" size="small" /> : <Text style={styles.btnText}>Continuer</Text>}
              </TouchableOpacity>

              <View style={styles.sepRow}>
                <View style={[styles.sepLine, { backgroundColor: colors.inputBorder }]} />
                <Text style={[styles.sepText, { color: colors.textSecondary }]}>ou</Text>
                <View style={[styles.sepLine, { backgroundColor: colors.inputBorder }]} />
              </View>

              <TouchableOpacity
                style={[styles.googleBtn, { borderColor: colors.inputBorder }, busy && styles.btnDisabled]}
                onPress={handleGoogle}
                disabled={busy}
                activeOpacity={0.8}
              >
                <Text style={[styles.googleG]}>G</Text>
                <Text style={[styles.googleBtnText, { color: colors.textPrimary }]}>Continuer avec Google</Text>
              </TouchableOpacity>
            </>
          )}

          {step === 'signup' && (
            <>
              <Text style={[styles.title, { color: colors.textPrimary }]}>Créer un compte</Text>
              <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
                Quelques informations pour finaliser votre inscription.
              </Text>
              {field('Prénom', givenName, setGivenName, { autoCapitalize: 'words' })}
              {field('Nom', surname, setSurname, { autoCapitalize: 'words' })}
              {field('Âge', age, setAge, { keyboardType: 'number-pad', maxLength: 3 })}

              {/* Sélecteur de pays filtrable */}
              <TouchableOpacity
                style={[styles.inputWrapper, styles.selectRow, { borderBottomColor: colors.inputBorder }]}
                onPress={() => setCountryOpen(o => !o)}
                disabled={busy}
                activeOpacity={0.7}
              >
                <Text style={[styles.input, { color: country ? colors.inputText : colors.inputPlaceholder }]}>
                  {countryName || 'Pays'}
                </Text>
                <Text style={{ color: colors.textSecondary }}>{countryOpen ? '▲' : '▼'}</Text>
              </TouchableOpacity>
              {countryOpen && (
                <View style={[styles.dropdown, { borderColor: colors.inputBorder, backgroundColor: colors.background }]}>
                  <TextInput
                    style={[styles.input, styles.search, { color: colors.inputText }]}
                    placeholder="Rechercher un pays…"
                    placeholderTextColor={colors.inputPlaceholder}
                    value={countryQuery}
                    onChangeText={setCountryQuery}
                    autoCapitalize="none"
                  />
                  <ScrollView style={styles.dropdownList} keyboardShouldPersistTaps="handled" nestedScrollEnabled>
                    {filteredCountries.map(c => (
                      <TouchableOpacity
                        key={c.code}
                        style={styles.option}
                        onPress={() => {
                          setCountry(c.code);
                          setCountryOpen(false);
                          setCountryQuery('');
                          setError('');
                        }}
                      >
                        <Text style={{ color: colors.textPrimary }}>{c.name}</Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                </View>
              )}

              {error ? <Text style={[styles.errorText, { color: colors.error }]}>{error}</Text> : null}
              <TouchableOpacity
                style={[styles.btn, { backgroundColor: colors.primary }, busy && styles.btnDisabled]}
                onPress={submitSignup}
                disabled={busy}
                activeOpacity={0.8}
              >
                {busy ? <ActivityIndicator color="#fff" size="small" /> : <Text style={styles.btnText}>Recevoir le code</Text>}
              </TouchableOpacity>
              <TouchableOpacity onPress={() => { setStep('email'); setError(''); }} disabled={busy} style={styles.linkBtn}>
                <Text style={[styles.linkText, { color: colors.textSecondary }]}>← Changer d'email</Text>
              </TouchableOpacity>
            </>
          )}

          {step === 'otp' && (
            <>
              <Text style={[styles.title, { color: colors.textPrimary }]}>Vérification</Text>
              <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
                Un code a été envoyé à {otpTarget || email}.
              </Text>
              {field('Code OTP', code, setCode, {
                keyboardType: 'number-pad',
                returnKeyType: 'done',
                onSubmitEditing: verify,
              })}
              {error ? <Text style={[styles.errorText, { color: colors.error }]}>{error}</Text> : null}
              <TouchableOpacity
                style={[styles.btn, { backgroundColor: colors.primary }, busy && styles.btnDisabled]}
                onPress={verify}
                disabled={busy}
                activeOpacity={0.8}
              >
                {busy ? <ActivityIndicator color="#fff" size="small" /> : <Text style={styles.btnText}>Vérifier</Text>}
              </TouchableOpacity>
              <TouchableOpacity onPress={() => { setStep('email'); setCode(''); setError(''); }} disabled={busy} style={styles.linkBtn}>
                <Text style={[styles.linkText, { color: colors.textSecondary }]}>← Changer d'email</Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1 },
  scroll: { flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: 24 },
  card: { width: '100%', maxWidth: 400 },
  title: { fontSize: 28, fontWeight: '700', marginBottom: 8 },
  subtitle: { fontSize: 15, lineHeight: 22, marginBottom: 24 },
  inputWrapper: { borderBottomWidth: 2, marginBottom: 14 },
  selectRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  input: { fontSize: 16, paddingVertical: 10, paddingHorizontal: 0, flex: 1 },
  search: { borderBottomWidth: 1, borderBottomColor: '#8884', marginBottom: 4 },
  dropdown: { borderWidth: 1, borderRadius: 8, marginTop: -8, marginBottom: 14, padding: 8 },
  dropdownList: { maxHeight: 220 },
  option: { paddingVertical: 10, paddingHorizontal: 4 },
  errorText: { fontSize: 13, marginBottom: 6 },
  btn: {
    marginTop: 8,
    borderRadius: 8,
    paddingVertical: 14,
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 4,
  },
  btnDisabled: { opacity: 0.6 },
  btnText: { color: '#fff', fontSize: 16, fontWeight: '600' },
  linkBtn: { marginTop: 14, alignItems: 'center' },
  linkText: { fontSize: 14, fontWeight: '600' },
  sepRow: { flexDirection: 'row', alignItems: 'center', marginVertical: 16 },
  sepLine: { flex: 1, height: 1 },
  sepText: { marginHorizontal: 12, fontSize: 13 },
  googleBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderRadius: 8,
    paddingVertical: 13,
  },
  googleG: { color: '#4285F4', fontSize: 18, fontWeight: '700', marginRight: 10 },
  googleBtnText: { fontSize: 16, fontWeight: '600' },
});
